import type { Identifier } from "ra-core";

/** Fiches voisines dans la page de la liste, pour passer de l'une à l'autre sans revenir à la liste. */
export const neighbours = (ids: Identifier[], current: Identifier) => {
  const index = ids.findIndex((id) => String(id) === String(current));
  if (index < 0) return { total: ids.length };
  return {
    previous: index > 0 ? ids[index - 1] : undefined,
    next: index < ids.length - 1 ? ids[index + 1] : undefined,
    position: index + 1,
    total: ids.length,
  };
};

/** Ce que l'on sait de l'élément qui a le focus, lu par `describeTarget`. */
export type KeyTarget = {
  tagName: string;
  isContentEditable?: boolean;
  role?: string | null;
  inDialog?: boolean;
};

export type KeyInput = {
  key: string;
  altKey: boolean;
  ctrlKey: boolean;
  metaKey: boolean;
  target: KeyTarget | null;
};

const EDITABLE = new Set(["INPUT", "TEXTAREA", "SELECT"]);
const OWN_KEYS = new Set(["menu", "menuitem", "listbox", "option", "combobox"]);

/** J ou ↓ : fiche suivante ; K ou ↑ : précédente ; Échap : fermer. Rien pendant une saisie ou dans un menu. */
export const navigationKey = ({
  key,
  altKey,
  ctrlKey,
  metaKey,
  target,
}: KeyInput): "next" | "previous" | "close" | null => {
  if (altKey || ctrlKey || metaKey) return null;
  if (target) {
    if (EDITABLE.has(target.tagName) || target.isContentEditable) return null;
    if (target.inDialog || (target.role && OWN_KEYS.has(target.role)))
      return null;
  }
  switch (key) {
    case "j":
    case "J":
    case "ArrowDown":
      return "next";
    case "k":
    case "K":
    case "ArrowUp":
      return "previous";
    case "Escape":
      return "close";
    default:
      return null;
  }
};

export const describeTarget = (
  element: EventTarget | null,
): KeyTarget | null =>
  element instanceof HTMLElement
    ? {
        tagName: element.tagName,
        isContentEditable: element.isContentEditable,
        role: element.getAttribute("role"),
        inDialog: !!element.closest(
          "[role=dialog],[role=alertdialog],[role=menu],[role=listbox]",
        ),
      }
    : null;
